import React, { useEffect, useMemo, useRef, useState } from 'react';

import DOMPurify from 'dompurify';
import { useForm } from 'react-hook-form';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import RichTextEditor from '~/components/Common/Forms/fields/RichTextEditor';
import MarkdownPreview from '~/components/Common/MarkdownPreview';

import { ContentSourceFieldSelection, ContentSourceInfo } from '~/services/AccommodationService';

import ContentSourceChip from './ContentSourceChip';

type CustomValueType = 'text' | 'markdown' | 'html';

const FIELD_LABEL_MAP: Record<string, string> = {
  address: 'Address',
  additionalDescription: 'Additional Description',
  description: 'Description',
  facilities: 'Facilities Description',
  finePrint: 'Fine Print',
  finePrintCheckIn: 'Fine Print: Check-in',
  finePrintCheckOut: 'Fine Print: Check-out',
  finePrintCheckInInstructions: 'Fine Print: Check-in Instructions',
  finePrintFees: 'Fine Print: Fees',
  finePrintPets: 'Fine Print: Pets',
  finePrintGeneral: 'Fine Print: General',
  finePrintSafety: 'Fine Print: Cleaning and Safety',
  finePrintPolicies: 'Fine Print: Policies',
  finePrintInclusions: 'Fine Print: Inclusions',
  finePrintCancellation: 'Fine Print: Cancellation Policy',
  finePrintDateChanges: 'Fine Print: Date Changes',
  highlights: 'Highlights',
  locationDescription: 'Location Description',
  size: 'Size',
  tagline: 'Tagline',
};

const NON_SELECTABLE_SOURCE_FIELDS = new Set(['amenities']);
const NONE_SOURCE_VALUE = '__none__';
const CUSTOM_SOURCE_VALUE = '__custom__';

export type SelectionMode = 'source' | 'custom' | 'none';

export interface FieldState {
  mode: SelectionMode;
  sourceId: string;
  customValue: string;
  customValueType: CustomValueType;
  originalCustomValue?: string;
  originalCustomValueType?: CustomValueType;
  isEditingCustom?: boolean;
}

function asCustomValueType(valueType: ContentSourceInfo['valueType'] | undefined): CustomValueType {
  if (valueType === 'markdown' || valueType === 'html') {
    return valueType;
  }

  return 'text';
}

export function initFieldState(
  activeSourceId: string | null,
  sourcesById?: ContentSourcesById,
  fallbackValueType?: ContentSourceFieldSelection['valueType'],
): FieldState {
  if (!activeSourceId) {
    return { mode: 'none', sourceId: '', customValue: '', customValueType: 'text' };
  }

  const activeSource = sourcesById?.[activeSourceId];
  if (activeSource?.type === 'custom') {
    const customValueType = asCustomValueType(fallbackValueType ?? activeSource.valueType);

    return {
      mode: 'custom',
      sourceId: activeSource.id,
      customValue: activeSource.value ?? '',
      customValueType,
      originalCustomValue: activeSource.value ?? '',
      originalCustomValueType: customValueType,
      isEditingCustom: false,
    };
  }

  return { mode: 'source', sourceId: activeSourceId, customValue: '', customValueType: 'text' };
}

export function hasFieldChanged(state: FieldState, activeSourceId: string | null): boolean {
  if (state.mode === 'custom') {
    if (state.originalCustomValue === undefined || state.originalCustomValueType === undefined) {
      return true;
    }
    return (
      state.sourceId !== activeSourceId ||
      state.customValue !== state.originalCustomValue ||
      state.customValueType !== state.originalCustomValueType
    );
  }
  if (state.mode === 'source' && state.sourceId) {
    return state.sourceId !== activeSourceId;
  }
  if (state.mode === 'none') {
    return Boolean(activeSourceId);
  }
  return false;
}

export function buildFieldStates(
  fields: Array<{ field: string; activeSourceId: string | null; valueType?: ContentSourceFieldSelection['valueType'] }>,
  sourcesById?: ContentSourcesById,
): Record<string, FieldState> {
  const states: Record<string, FieldState> = {};
  for (const f of fields) {
    states[f.field] = initFieldState(f.activeSourceId, sourcesById, f.valueType);
  }
  return states;
}

export interface ChangedField<Field extends string = string> {
  field: Field;
  contentSourceId?: string | null;
  customValue?: string;
  customValueType?: CustomValueType;
}

export function buildChangedFields<Field extends string>(
  fields: Array<{ field: Field; activeSourceId: string | null }>,
  fieldStates: Record<string, FieldState>,
  options?: { clearedContentSourceId?: null },
): Array<ChangedField<Field>> {
  return fields
    .filter((f) => {
      const state = fieldStates[f.field];
      return state && hasFieldChanged(state, f.activeSourceId);
    })
    .map((f) => {
      const state = fieldStates[f.field];
      if (state.mode === 'custom') {
        return {
          field: f.field,
          customValue: state.customValue,
          customValueType: state.customValueType,
        };
      }
      if (state.mode === 'none') {
        return {
          field: f.field,
          contentSourceId: options?.clearedContentSourceId,
        };
      }
      return {
        field: f.field,
        contentSourceId: state.sourceId,
      };
    });
}

type ContentSourcesById = Record<string, ContentSourceInfo>;

export function getActiveSource(
  fieldSelection: ContentSourceFieldSelection | undefined,
  sourcesById: ContentSourcesById,
): ContentSourceInfo | null {
  if (!fieldSelection?.activeSourceId) {
    return null;
  }

  return sourcesById[fieldSelection.activeSourceId] ?? null;
}

export function getSelectableSources(
  fieldSelection: ContentSourceFieldSelection,
  sourcesById: ContentSourcesById,
): Array<ContentSourceInfo> {
  const sources = fieldSelection.availableSourceIds
    .map((sourceId) => sourcesById[sourceId])
    .filter((source): source is ContentSourceInfo => Boolean(source));
  const activeSource = getActiveSource(fieldSelection, sourcesById);

  if (activeSource && !sources.some((source) => source.id === activeSource.id)) {
    return [activeSource, ...sources];
  }

  return sources;
}

type SourceValueType = NonNullable<ContentSourceInfo['valueType']>;

interface SourceValuePreviewProps {
  value: string;
  valueType: SourceValueType | null;
  color?: 'text.primary' | 'text.secondary';
}

function SourceValuePreview({ value, valueType, color = 'text.secondary' }: SourceValuePreviewProps) {
  if (valueType === 'markdown') {
    return (
      <Box sx={{ typography: 'body2', color, whiteSpace: 'pre-wrap', '& p': { my: 0 } }}>
        <MarkdownPreview content={value} />
      </Box>
    );
  }

  if (valueType === 'html') {
    return <Box sx={{ typography: 'body2', color }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />;
  }

  return (
    <Typography variant="body2" color={color} sx={{ whiteSpace: 'pre-wrap' }}>
      {value}
    </Typography>
  );
}

interface ContentSourceFieldRowProps {
  fieldSelection: ContentSourceFieldSelection;
  activeSource: ContentSourceInfo | null;
  availableSources: Array<ContentSourceInfo>;
  fieldState: FieldState;
  onChange: (field: string, state: FieldState) => void;
  isPending: boolean;
  hideLabel?: boolean;
  labelOverride?: string;
  allowEditSourceAsCustom?: boolean;
}

export default function ContentSourceFieldRow({
  fieldSelection,
  activeSource,
  availableSources,
  fieldState,
  onChange,
  isPending,
  hideLabel = false,
  labelOverride,
  allowEditSourceAsCustom = false,
}: ContentSourceFieldRowProps) {
  const { field, activeSourceId } = fieldSelection;
  const fieldCustomValueType = fieldSelection.valueType;
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const customFormFieldName = `${field}CustomValue`;
  const { control, reset, watch } = useForm<Record<string, string>>({
    defaultValues: { [customFormFieldName]: fieldState.customValue },
  });
  const customEditorValue = watch(customFormFieldName);
  const skipNextEditorSync = useRef(false);
  const {
    customValue,
    customValueType,
    isEditingCustom,
    mode,
    originalCustomValue,
    originalCustomValueType,
    sourceId,
  } = fieldState;

  useEffect(() => {
    skipNextEditorSync.current = true;
    reset({ [customFormFieldName]: customValue });
  }, [customFormFieldName, customValue, reset]);

  useEffect(() => {
    if (skipNextEditorSync.current) {
      skipNextEditorSync.current = false;
      return;
    }

    if (mode === 'custom' && customEditorValue !== customValue) {
      onChange(field, {
        mode,
        sourceId,
        customValue: customEditorValue ?? '',
        customValueType,
        originalCustomValue,
        originalCustomValueType,
        isEditingCustom,
      });
    }
  }, [
    customEditorValue,
    customValue,
    customValueType,
    field,
    isEditingCustom,
    mode,
    onChange,
    originalCustomValue,
    originalCustomValueType,
    sourceId,
  ]);

  const sourcesWithValue = useMemo(
    () =>
      availableSources.filter(
        (source) => source.value?.trim() && !NON_SELECTABLE_SOURCE_FIELDS.has(source.field.toLowerCase()),
      ),
    [availableSources],
  );

  const filteredSources = useMemo(() => {
    if (!sourceSearchTerm.trim()) {
      return sourcesWithValue;
    }
    const normalizedSearchTerm = sourceSearchTerm.toLowerCase();
    return sourcesWithValue.filter((source) => {
      const fieldText = source.field.toLowerCase();
      const valueText = source.value?.toLowerCase() ?? '';
      return fieldText.includes(normalizedSearchTerm) || valueText.includes(normalizedSearchTerm);
    });
  }, [sourceSearchTerm, sourcesWithValue]);

  const selectedSource = useMemo(
    () => sourcesWithValue.find((s) => s.id === fieldState.sourceId),
    [sourcesWithValue, fieldState.sourceId],
  );
  const availableCustomSource = useMemo(
    () => sourcesWithValue.find((source) => source.type === 'custom'),
    [sourcesWithValue],
  );

  const changed = hasFieldChanged(fieldState, activeSourceId);
  const hasSelectedSourceInOptions = sourcesWithValue.some((source) => source.id === fieldState.sourceId);
  const sourceSelectValue =
    fieldState.mode === 'custom'
      ? (availableCustomSource?.id ?? CUSTOM_SOURCE_VALUE)
      : fieldState.mode === 'none'
        ? NONE_SOURCE_VALUE
        : hasSelectedSourceInOptions
          ? fieldState.sourceId
          : '';

  const handleSourceChange = (sourceId: string) => {
    if (sourceId === NONE_SOURCE_VALUE) {
      onChange(field, {
        ...fieldState,
        mode: 'none',
        sourceId: '',
        customValue: '',
        isEditingCustom: false,
      });
    } else if (sourceId === CUSTOM_SOURCE_VALUE) {
      const displayedNonCustomSource =
        fieldState.mode === 'source'
          ? (sourcesWithValue.find((source) => source.id === fieldState.sourceId) ??
            (fieldState.sourceId === activeSourceId ? activeSource : null))
          : null;
      const prefill = activeSource?.type === 'custom' ? activeSource : displayedNonCustomSource;
      const customValueType = asCustomValueType(fieldCustomValueType ?? prefill?.valueType);
      onChange(field, {
        ...fieldState,
        mode: 'custom',
        sourceId: activeSource?.type === 'custom' ? activeSource.id : '',
        customValue: prefill?.value ?? '',
        customValueType,
        originalCustomValue: activeSource?.type === 'custom' ? (activeSource.value ?? '') : undefined,
        originalCustomValueType: activeSource?.type === 'custom' ? customValueType : undefined,
        isEditingCustom: true,
      });
    } else {
      const selectedSource = sourcesWithValue.find((source) => source.id === sourceId);
      if (selectedSource?.type === 'custom') {
        const customValueType = asCustomValueType(fieldCustomValueType ?? selectedSource.valueType);
        onChange(field, {
          ...fieldState,
          mode: 'custom',
          sourceId: selectedSource.id,
          customValue: selectedSource.value ?? '',
          customValueType,
          originalCustomValue: selectedSource.value ?? '',
          originalCustomValueType: customValueType,
          isEditingCustom: false,
        });
        setSourceSearchTerm('');
        return;
      }

      onChange(field, { ...fieldState, mode: 'source', sourceId, isEditingCustom: false });
    }
    setSourceSearchTerm('');
  };

  const displayedSource =
    fieldState.mode === 'source'
      ? (selectedSource ?? (fieldState.sourceId === activeSourceId ? activeSource : null))
      : null;
  const previewValue =
    fieldState.mode === 'custom'
      ? fieldState.customValue
      : fieldState.mode === 'source'
        ? displayedSource?.value
        : null;
  const previewValueType =
    fieldState.mode === 'custom'
      ? fieldState.customValueType
      : fieldState.mode === 'source'
        ? displayedSource?.valueType
        : null;
  const headerSourceType = fieldState.mode === 'custom' ? 'custom' : displayedSource?.type;

  const handleEditSelectedContent = () => {
    if (fieldState.mode === 'custom') {
      onChange(field, { ...fieldState, isEditingCustom: true });
      return;
    }

    if (!allowEditSourceAsCustom || fieldState.mode !== 'source' || !displayedSource) {
      return;
    }

    const customValueType = asCustomValueType(fieldCustomValueType ?? displayedSource.valueType);
    const originalCustomValueType = availableCustomSource
      ? asCustomValueType(fieldCustomValueType ?? availableCustomSource.valueType)
      : undefined;

    onChange(field, {
      ...fieldState,
      mode: 'custom',
      sourceId: availableCustomSource?.id ?? '',
      customValue: displayedSource.value ?? '',
      customValueType,
      originalCustomValue: availableCustomSource?.value,
      originalCustomValueType,
      isEditingCustom: true,
    });
  };

  const canEditSelectedContent =
    !isPending &&
    (fieldState.mode === 'custom' ||
      (allowEditSourceAsCustom && fieldState.mode === 'source' && Boolean(displayedSource)));

  const renderCustomEditorInput = () => (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
      <Box flex={1} width="100%">
        {fieldState.customValueType === 'markdown' ? (
          <RichTextEditor control={control} name={customFormFieldName} label="Custom content" disabled={isPending} />
        ) : (
          <TextField
            multiline
            minRows={2}
            maxRows={8}
            fullWidth
            size="small"
            label="Custom content"
            value={fieldState.customValue}
            onChange={(e) => onChange(field, { ...fieldState, customValue: e.target.value })}
            disabled={isPending}
          />
        )}
      </Box>
    </Stack>
  );

  const renderSelectedContent = (asCard: boolean) => {
    if (asCard && !previewValue && fieldState.mode !== 'custom') {
      return null;
    }

    const content =
      asCard && fieldState.mode === 'custom' && fieldState.isEditingCustom ? (
        renderCustomEditorInput()
      ) : previewValue ? (
        <SourceValuePreview value={previewValue} valueType={previewValueType ?? null} />
      ) : (
        <Typography variant="body2" color="text.disabled">
          {fieldState.mode === 'custom' ? 'No custom content entered' : 'No source selected'}
        </Typography>
      );

    if (!asCard) {
      return content;
    }

    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1}>
            <Typography variant="caption" color="text.secondary" display="block">
              Selected Content
            </Typography>
            {canEditSelectedContent && !fieldState.isEditingCustom && (
              <Button size="small" variant="text" onClick={handleEditSelectedContent}>
                Edit
              </Button>
            )}
          </Stack>
          {content}
        </CardContent>
      </Card>
    );
  };

  const renderSourceSelector = () => (
    <FormControl size="small" sx={{ minWidth: 280 }}>
      <InputLabel>Source</InputLabel>
      <Select
        value={sourceSelectValue}
        label="Source"
        onChange={(e) => handleSourceChange(e.target.value)}
        onClose={() => setSourceSearchTerm('')}
        disabled={isPending}
        MenuProps={{
          autoFocus: false,
          MenuListProps: { autoFocusItem: false },
        }}
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder="Quick search"
            value={sourceSearchTerm}
            onChange={(event) => setSourceSearchTerm(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
        </ListSubheader>
        <MenuItem value={NONE_SOURCE_VALUE}>
          <Typography variant="body2" fontStyle="italic">
            No source
          </Typography>
        </MenuItem>
        {filteredSources.map((source) => (
          <MenuItem key={source.id} value={source.id}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ContentSourceChip type={source.type} />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                {source.field}
              </Typography>
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {source.value?.slice(0, 50)}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
        {filteredSources.length === 0 && (
          <MenuItem disabled value="__no_results__">
            <Typography variant="body2" color="text.secondary">
              No matching sources
            </Typography>
          </MenuItem>
        )}
        {!availableCustomSource && (
          <MenuItem value={CUSTOM_SOURCE_VALUE}>
            <Typography variant="body2" fontStyle="italic">
              Custom value...
            </Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );

  const renderCustomEditor = () => (
    <Collapse in={fieldState.mode === 'custom' && Boolean(fieldState.isEditingCustom)}>
      <Box mt={2}>{renderCustomEditorInput()}</Box>
    </Collapse>
  );

  const renderControls = () => (
    <>
      <Stack direction="row" spacing={2} mt={2} alignItems="flex-start">
        {renderSourceSelector()}
      </Stack>
      {(hideLabel || fieldState.mode !== 'custom') && renderCustomEditor()}
    </>
  );

  if (!hideLabel) {
    return (
      <Accordion defaultExpanded={changed || Boolean(fieldState.isEditingCustom)} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1">{labelOverride ?? FIELD_LABEL_MAP[field] ?? field}</Typography>
            {headerSourceType && <ContentSourceChip type={headerSourceType} />}
            {changed && (
              <Typography variant="caption" color="warning.main" fontWeight="bold">
                unsaved
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {renderSelectedContent(true)}
            {renderControls()}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box sx={{ py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box flex={1}>{renderSelectedContent(false)}</Box>
      {renderControls()}
    </Box>
  );
}
